import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiPause, FiSend, FiXCircle } from 'react-icons/fi';
import { push, ref, set } from 'firebase/database';
import { db } from '../../firebaseConfig';
import { updateOutreachRecipient, updateOutreachSequence } from '../applicationApi';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import { useLanguage } from '../contexts/LanguageContext';
import MobileHelpSlot from '../components/MobileHelpSlot';

const MOBILE_BREAKPOINT = 760;
function formatTime(timestamp, language = 'en') {
  if (!timestamp) return '';

  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

function formatDateLabel(timestamp, language = 'en') {
  if (!timestamp) return '';

  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function normalizeMessage([id, value]) {
  return {
    id,
    message: value.message || '',
    timestamp: value.timestamp || 0,
    user: value.user || '',
  };
}

export function normalizeApplicationsPayload(payload = {}) {
  return Object.entries(payload)
    .map(([applicationId, value]) => {
      const application = value && typeof value === 'object' ? value : {};

      return {
        ...application,
        // Application IDs are job IDs; chat paths must use the separately
        // persisted deterministic connection/thread identifier.
        id: application.id || applicationId,
        jobId: application.jobId || applicationId,
        threadId: application.threadId || application.connectionKey || '',
      };
    })
    .filter((application) => application.canMessage && application.threadId)
    .sort((left, right) => (right.lastUpdated || right.appliedAt || 0) - (left.lastUpdated || left.appliedAt || 0));
}

function normalizeSequencesPayload(payload = {}) {
  return Object.entries(payload)
    .map(([id, value]) => ({ id, ...value }))
    .sort((left, right) => (right.updatedAt || right.createdAt || 0) - (left.updatedAt || left.createdAt || 0));
}

function normalizeMessagesPayload(payload = {}) {
  return Object.entries(payload)
    .map(normalizeMessage)
    .sort((left, right) => left.timestamp - right.timestamp);
}

function renderMessageContent(message) {
  const parts = message.split(/(https?:\/\/[^\s]+)/g);

  return parts.map((part, index) => {
    if (/^https?:\/\/[^\s]+$/.test(part)) {
      return (
        <a key={`${part}-${index}`} href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      );
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;
}

function InboxScreen({ user, initialTab = 'messages' }) {
  const { language, t } = useLanguage();
  const ti = (key) => t(`inboxPage.${key}`);
  const interpolate = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const number = (value) => new Intl.NumberFormat(language === 'ar' ? 'ar-LB' : 'en-US').format(value || 0);
  const applicationsPath = user?.uid ? `people/${user.uid}/application_index/applications` : '';
  const sequencesPath = user?.uid ? `people/${user.uid}/outreach_sequences` : '';
  const cachedApplicationsRaw = applicationsPath ? getCachedFirebaseValue(applicationsPath) : undefined;
  const cachedApplications = cachedApplicationsRaw !== undefined
    ? normalizeApplicationsPayload(cachedApplicationsRaw || {})
    : null;
  const cachedSequencesRaw = sequencesPath ? getCachedFirebaseValue(sequencesPath) : undefined;
  const initialMobile = isMobileViewport();
  const initialSelectedId = initialMobile ? '' : cachedApplications?.[0]?.id || '';
  const initialSelectedApplication = cachedApplications?.find((application) => application.id === initialSelectedId);
  const initialMessagesPath = initialSelectedApplication?.threadId
    ? `connections/${initialSelectedApplication.threadId}/messages`
    : '';
  const cachedMessagesRaw = initialMessagesPath ? getCachedFirebaseValue(initialMessagesPath) : undefined;
  const [applications, setApplications] = useState(cachedApplications || []);
  const [messages, setMessages] = useState(() => normalizeMessagesPayload(cachedMessagesRaw || {}));
  const [threadPreviews, setThreadPreviews] = useState(() => (cachedApplications || []).reduce((previews, application) => {
    const cachedThread = getCachedFirebaseValue(`connections/${application.threadId}/messages`);
    if (cachedThread === undefined) return previews;
    const threadMessages = normalizeMessagesPayload(cachedThread || {});
    previews[application.id] = threadMessages[threadMessages.length - 1] || null;
    return previews;
  }, {}));
  const [outreachSequences, setOutreachSequences] = useState(() => normalizeSequencesPayload(cachedSequencesRaw || {}));
  const [outreachLoaded, setOutreachLoaded] = useState(!user?.uid || cachedSequencesRaw !== undefined);
  const [messagesLoaded, setMessagesLoaded] = useState(!initialMessagesPath || cachedMessagesRaw !== undefined);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedId, setSelectedId] = useState(initialSelectedId);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(!cachedApplications);
  const [showThread, setShowThread] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isMobileRef = useRef(initialMobile);
  const selectedThreadId = applications.find((application) => application.id === selectedId)?.threadId || '';

  useEffect(() => {
    if (!user?.uid) return;

    if (isMobileRef.current) {
      setSelectedId('');
      setShowThread(false);
    }

    let receivedFirstSnapshot = false;
    const applicationsRef = ref(db, applicationsPath);
    const unsubscribe = subscribeCachedValue(applicationsRef, applicationsPath, (snapshot) => {
      const payload = snapshot.val() || {};
      const nextApplications = normalizeApplicationsPayload(payload);

      setApplications(nextApplications);

      if (!isMobileRef.current) {
        setSelectedId((current) => {
          if (current && nextApplications.some((application) => application.id === current)) {
            return current;
          }

          return nextApplications[0]?.id || '';
        });
      } else {
        setSelectedId('');
      }

      if (!receivedFirstSnapshot) {
        receivedFirstSnapshot = true;
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [user?.uid, applicationsPath]);

  useEffect(() => {
    if (!user?.uid) return undefined;

    const sequencesRef = ref(db, sequencesPath);
    const unsubscribe = subscribeCachedValue(sequencesRef, sequencesPath, (snapshot) => {
      const payload = snapshot.val() || {};
      const nextSequences = normalizeSequencesPayload(payload);
      setOutreachSequences(nextSequences);
      setOutreachLoaded(true);
    });

    return unsubscribe;
  }, [user?.uid, sequencesPath]);

  useEffect(() => {
    if (!selectedThreadId) {
      setMessages([]);
      setMessagesLoaded(true);
      return undefined;
    }

    const messagesPath = `connections/${selectedThreadId}/messages`;
    if (getCachedFirebaseValue(messagesPath) === undefined) {
      setMessages([]);
      setMessagesLoaded(false);
    }
    const messagesRef = ref(db, messagesPath);
    const unsubscribe = subscribeCachedValue(messagesRef, messagesPath, (snapshot) => {
      const payload = snapshot.val() || {};
      const nextMessages = normalizeMessagesPayload(payload);

      setMessages(nextMessages);
      setMessagesLoaded(true);
    });

    return unsubscribe;
  }, [selectedThreadId]);

  useEffect(() => {
    const unsubscribes = applications.map((application) => {
      const messagesPath = `connections/${application.threadId}/messages`;
      const messagesRef = ref(db, messagesPath);

      return subscribeCachedValue(messagesRef, messagesPath, (snapshot) => {
        const payload = snapshot.val() || {};
        const nextMessages = normalizeMessagesPayload(payload);
        setThreadPreviews((current) => ({
          ...current,
          [application.id]: nextMessages[nextMessages.length - 1] || null,
        }));
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [applications]);

  const selectedApplication = useMemo(
    () => applications.find((application) => application.id === selectedId),
    [applications, selectedId],
  );

  const handleSelect = (applicationId) => {
    setSelectedId(applicationId);
    window.requestAnimationFrame(() => {
      setShowThread(true);
    });
  };

  const handleBackToList = () => {
    setShowThread(false);
  };

  const handleSend = async (event) => {
    event.preventDefault();

    const nextMessage = draft.trim();
    if (!nextMessage || !selectedThreadId || !user?.uid) return;

    setDraft('');

    await set(push(ref(db, `connections/${selectedThreadId}/messages`)), {
      message: nextMessage,
      timestamp: Date.now(),
      user: user.uid,
    });
  };

  const handleSequenceStatus = async (sequenceId, status) => {
    await updateOutreachSequence({ user, sequenceId, status });
  };

  const handleRecipientStatus = async (sequenceId, recipientId, status) => {
    await updateOutreachRecipient({ user, sequenceId, recipientId, status });
  };

  const handleDraftKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend(event);
    }
  };

  useEffect(() => {
    if (!textareaRef.current) return;

    textareaRef.current.style.height = '0px';
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 180)}px`;
  }, [draft]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, selectedId]);

  if (loading) {
    return (
      <section className="js-inbox-loading" aria-label={ti('loading')}>
        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
        <p>{ti('loading')}</p>
      </section>
    );
  }

  return (
    <section className={`js-inbox ${showThread ? 'is-thread-open' : ''}`} aria-label={ti('label')}>
      <div className="js-inbox__list">
        <h1 className="js-section-title">{ti('title')}</h1>
        <p className="js-inbox__subtitle">{ti('subtitle')}</p>
        <div className="js-inbox-tabs" role="tablist" aria-label={ti('sections')}>
          <button
            type="button"
            className={activeTab === 'messages' ? 'is-active' : ''}
            onClick={() => setActiveTab('messages')}
          >
            {ti('messages')}
          </button>
          <button
            type="button"
            className={activeTab === 'outreach' ? 'is-active' : ''}
            onClick={() => setActiveTab('outreach')}
          >
            {ti('outreach')}
          </button>
        </div>

        {activeTab === 'messages' ? (
          <div className="js-inbox__threads">
          {!loading && applications.length === 0 && (
            <div className="js-inbox__empty">{ti('noConversations')}</div>
          )}

            {applications.map((application) => {
              const lastMessage = threadPreviews[application.id];
              const previewLoaded = Object.prototype.hasOwnProperty.call(threadPreviews, application.id);

              return (
                <button
                  type="button"
                  key={application.id}
                  className={`js-inbox-thread ${application.id === selectedId ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(application.id)}
                >
                  <span className="js-inbox-thread__top">
                    <strong dir="auto">{application.companyName || ti('company')}</strong>
                    <span>{formatTime(lastMessage?.timestamp || application.lastUpdated || application.appliedAt, language)}</span>
                  </span>
                  <span className="js-inbox-thread__role" dir="auto">{application.jobTitle || ti('rolePending')}</span>
                  <span className="js-inbox-thread__preview">
                    {application.hasUnreadMessages && <i aria-label={ti('unread')} />}
                    {lastMessage?.message || (previewLoaded ? ti('noMessages') : ti('loadingMessages'))}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="js-outreach-list">
            {outreachLoaded && outreachSequences.length === 0 && (
              <div className="js-inbox__empty">{ti('noOutreach')}</div>
            )}
            {!outreachLoaded && <div className="js-personnel-spinner" role="status" aria-label={ti('loadingOutreach')} />}
            {outreachSequences.map((sequence) => (
              <article className="js-outreach-sequence" key={sequence.id}>
                <span className="js-outreach-sequence__top">
                  <strong dir="auto">{sequence.companyName || ti('company')}</strong>
                  <em className={`is-${sequence.status}`}>{ti(`status.${sequence.status || 'active'}`)}</em>
                </span>
                <span dir="auto">{sequence.jobTitle || ti('rolePending')}</span>
                {sequence.companyDescription && (
                  <p className="js-outreach-sequence__description">{sequence.companyDescription}</p>
                )}
                <small>{interpolate(ti('recipientsEmails'), { recipients: number((sequence.recipients || []).length), emails: number((sequence.drafts || []).length) })}</small>
                <div className="js-outreach-contacts" aria-label={ti('contacts')}>
                  {(sequence.recipients || []).map((recipient) => (
                    <div className="js-outreach-contact" key={recipient.id || recipient.email || recipient.name}>
                      <span>
                        <strong dir="auto">{recipient.name || recipient.personName || ti('contact')}</strong>
                        <small dir="auto">{recipient.title || ti('hiringTeam')}</small>
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRecipientStatus(sequence.id, recipient.id, 'cancelled')}
                        disabled={!recipient.id || recipient.status === 'cancelled' || sequence.status === 'cancelled'}
                      >
                        {ti('cancelContact')}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="js-outreach-sequence__actions">
                  <button type="button" onClick={() => handleSequenceStatus(sequence.id, 'paused')} disabled={sequence.status === 'paused' || sequence.status === 'cancelled'}>
                    <FiPause aria-hidden="true" />
                    <span>{ti('pause')}</span>
                  </button>
                  <button type="button" onClick={() => handleSequenceStatus(sequence.id, 'cancelled')} disabled={sequence.status === 'cancelled'}>
                    <FiXCircle aria-hidden="true" />
                    <span>{ti('cancel')}</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <div className="js-inbox__chat">
        {selectedApplication ? (
          <>
            <header className="js-chat-head">
              <button type="button" className="js-chat-head__back" onClick={handleBackToList}>
                <FiArrowLeft className="js-icon--directional" aria-hidden="true" />
                <span>{ti('back')}</span>
              </button>
              <MobileHelpSlot active={showThread} />
              <div>
                <h2 dir="auto">{selectedApplication.companyName || ti('company')}</h2>
                <p dir="auto">{selectedApplication.jobTitle || ti('rolePending')}</p>
              </div>
            </header>

            <div className="js-chat-messages">
              {messagesLoaded && messages.length === 0 && (
                <div className="js-inbox__empty">{ti('noThreadMessages')}</div>
              )}
              {!messagesLoaded && <div className="js-personnel-spinner" role="status" aria-label={ti('loadingMessages')} />}

              {messages.map((message, index) => {
                const outgoing = message.user === user?.uid;
                const previousMessage = messages[index - 1];
                const currentDate = formatDateLabel(message.timestamp, language);
                const previousDate = previousMessage ? formatDateLabel(previousMessage.timestamp, language) : '';
                const showDateDivider = currentDate !== previousDate;

                return (
                  <React.Fragment key={message.id}>
                    {showDateDivider && <div className="js-chat-date">{currentDate}</div>}
                    <article className={`js-chat-message ${outgoing ? 'is-outgoing' : ''}`}>
                      <p>{renderMessageContent(message.message)}</p>
                      <time>{formatTime(message.timestamp, language)}</time>
                    </article>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form className="js-chat-compose" onSubmit={handleSend}>
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleDraftKeyDown}
                placeholder={ti('typeMessage')}
                aria-label={ti('message')}
              />
              <button type="submit" aria-label={ti('send')}>
                <FiSend aria-hidden="true" />
              </button>
            </form>
          </>
        ) : (
          <div className="js-inbox__empty">{ti('selectCompany')}</div>
        )}
      </div>
    </section>
  );
}

export default InboxScreen;
